// API error handling — matches Cloudflare Worker error codes

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  /**
   * @param {number} status - HTTP status code
   * @param {string} message - Error message
   * @param {Object} data - Additional error data ({ code, details })
   */
  constructor(status, message, data = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = data.code || null;
    this.data = data;
  }

  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  get isValidationError() {
    return this.status === 400 || this.status === 422;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isServerError() {
    return this.status >= 500;
  }

  get isNetworkError() {
    return this.status === 0;
  }

  get isRateLimited() {
    return this.status === 429 || this.code === 'RATE_LIMITED';
  }

  get isInsufficientCredits() {
    return this.code === 'INSUFFICIENT_CREDITS';
  }

  get isConcurrentLimit() {
    return this.code === 'CONCURRENT_LIMIT';
  }
}

/**
 * Worker API error codes (from api-worker error.sanitizer.ts)
 */
export const ERROR_CODES = {
  // Auth
  MISSING_TOKEN: 'MISSING_TOKEN',
  INVALID_TOKEN: 'INVALID_TOKEN',
  AUTH_FAILED: 'AUTH_FAILED',
  NO_TOKEN: 'NO_TOKEN',

  // Business logic
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  CONCURRENT_LIMIT: 'CONCURRENT_LIMIT',
  JOB_IN_PROGRESS: 'JOB_IN_PROGRESS',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',

  // Not found
  NOT_FOUND: 'NOT_FOUND',
  JOB_NOT_FOUND: 'JOB_NOT_FOUND',

  // Validation
  INVALID_JOB_ID: 'INVALID_JOB_ID',
  INVALID_PAGINATION: 'INVALID_PAGINATION',

  // Generic
  INTERNAL_ERROR: 'INTERNAL_ERROR',
};

/**
 * Get user-friendly error message based on error type/code
 * @param {Error} error
 * @returns {string}
 */
export function getErrorMessage(error) {
  if (error instanceof ApiError) {
    // Check error code first (more specific)
    if (error.code) {
      switch (error.code) {
        case ERROR_CODES.INSUFFICIENT_CREDITS:
          return 'Not enough credits. Please purchase more.';
        case ERROR_CODES.CONCURRENT_LIMIT:
          return 'You have too many videos generating. Please wait for one to complete.';
        case ERROR_CODES.JOB_IN_PROGRESS:
          return 'You already have a video generating. Please wait.';
        case ERROR_CODES.RATE_LIMITED:
          return 'Too many requests. Please try again later.';
        case ERROR_CODES.NO_TOKEN:
        case ERROR_CODES.MISSING_TOKEN:
        case ERROR_CODES.INVALID_TOKEN:
          return 'Please sign in to continue.';
      }
    }

    // Fall back to HTTP status
    if (error.isNetworkError) return 'Connection error. Please check your internet connection.';
    if (error.isAuthError) return 'Your session has expired. Please log in again.';
    if (error.isRateLimited) return 'Too many requests. Please try again later.';
    if (error.isServerError) return 'Server error. Please try again later.';
    return error.message;
  }
  return 'An unexpected error occurred.';
}
